/**
 * CFS collection page behaviour (build spec §4.5).
 *
 * <cfs-stock-toggle>: "Hide out of stock" switch bound to the Availability filter. It ticks the
 * filter's own "In stock" input in Horizon's facets form (so results update without a reload and
 * the URL keeps the state); if that filter isn't configured it falls back to the URL parameter.
 *
 * Back-button position: when a product in an infinite-scroll grid is opened, the current URL is
 * replaced with ?page=N#{grid item id}, as Horizon's own product card does, so Back returns to the
 * same page of results with that product in view (sections/main-collection.liquid scrolls to it).
 *
 * Back/forward through filter changes: Horizon pushes a history entry per filter change but does
 * not re-render on popstate, so the results here are re-rendered for the restored URL.
 */
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

class CfsStockToggle extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('input'));
    this.param = this.dataset.param || 'filter.v.availability';
    this.input.addEventListener('change', this.#onChange);
    document.addEventListener(StandardEvents.collectionUpdate, this.#sync);
    window.addEventListener('popstate', this.#sync);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.collectionUpdate, this.#sync);
    window.removeEventListener('popstate', this.#sync);
  }

  /** The "In stock" input of the Availability filter in a visible facets form. */
  #filterInput() {
    const inputs = /** @type {HTMLInputElement[]} */ ([
      ...document.querySelectorAll(`facets-form-component input[name="${this.param}"][value="1"]`),
    ]);
    return inputs.find((input) => input.closest('form')?.getClientRects().length) ?? inputs[0] ?? null;
  }

  #busy = false;

  #onChange = async () => {
    const want = this.input.checked;
    if (this.#busy) {
      this.input.checked = !want;
      return;
    }
    // Part-way down the list: keep the shopper's place (owner, 10 Oct 2026)
    const anchor = captureAnchor();
    if (anchor) {
      this.#busy = true;
      try {
        if (await this.#toggleKeepingPlace(want, anchor)) return;
      } catch (e) {
        // falls back to Horizon's own update below
      } finally {
        this.#busy = false;
      }
    }
    const filterInput = this.#filterInput();
    if (filterInput) {
      if (filterInput.checked !== want) {
        filterInput.checked = want;
        filterInput.dispatchEvent(new Event('change', { bubbles: true }));
        filterInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(this.param);
    url.searchParams.delete('page');
    if (want) url.searchParams.append(this.param, '1');
    window.location.assign(url.toString());
  };

  /**
   * Re-renders the results with the switch applied, from the page of the new list that holds the
   * product at the top of the screen (or, when hiding, the next one still in stock), and puts that
   * product back where it was. Horizon's infinite scroll then loads the pages above and below it.
   * @param {boolean} want
   * @param {NonNullable<ReturnType<typeof captureAnchor>>} anchor
   * @returns {Promise<boolean>} false when no anchor product was found (caller falls back)
   */
  async #toggleKeepingPlace(want, anchor) {
    const base = new URL(window.location.href);
    base.searchParams.delete(this.param);
    base.searchParams.delete('page');
    base.hash = '';
    if (want) base.searchParams.append(this.param, '1');
    /** @param {number} page */
    const pageUrl = (page) => {
      const url = new URL(base);
      if (page > 1) url.searchParams.set('page', String(page));
      return url;
    };
    /** @param {number} page */
    const read = async (page) => {
      const html = await sectionRenderer.getSectionHTML(anchor.sectionId, true, pageUrl(page));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const ids = [...doc.querySelectorAll('.product-grid__item[data-product-id]')].map((el) => /** @type {HTMLElement} */ (el).dataset.productId);
      return { page, ids, last: Number(doc.querySelector('[data-last-page]')?.getAttribute('data-last-page')) || 1 };
    };

    anchor.list.setAttribute('aria-busy', 'true');
    let found = /** @type {{ page: number, id: string } | null} */ (null);
    try {
      if (want) {
        // Hiding: the products above can only move up, so the anchor's new page is at most its old one.
        const pages = await Promise.all(Array.from({ length: anchor.page }, (_, i) => read(i + 1)));
        let best = Infinity;
        for (const { page, ids } of pages) {
          for (const id of ids) {
            const rank = anchor.ids.indexOf(id);
            if (rank !== -1 && rank < best) {
              best = rank;
              found = { page, id };
            }
          }
        }
      } else {
        // Showing: the anchor can only move down; look from its old page onwards, three pages at a time.
        const id = anchor.ids[0];
        for (let start = anchor.page, last = Infinity; !found && start <= Math.min(last, anchor.page + 30); start += 3) {
          const batch = [start, start + 1, start + 2].filter((page) => page <= last);
          const pages = await Promise.all(batch.map(read));
          last = pages[0]?.last ?? 1;
          const hit = pages.find((p) => p.ids.includes(id));
          if (hit) found = { page: hit.page, id };
        }
      }
      if (!found) return false;

      for (const input of document.querySelectorAll(`cfs-stock-toggle input`)) /** @type {HTMLInputElement} */ (input).checked = want;
      const target = pageUrl(found.page);
      history.pushState({ urlParameters: target.searchParams.toString() }, '', target.toString());
      await sectionRenderer.renderSection(anchor.sectionId);
      const card = document.querySelector(`.product-grid__item[data-product-id="${found.id}"]`);
      if (card) window.scrollBy({ top: card.getBoundingClientRect().top - anchor.top, behavior: 'instant' });
      return true;
    } finally {
      document.querySelector('results-list[section-id]')?.removeAttribute('aria-busy');
      anchor.list.removeAttribute('aria-busy');
    }
  }

  #sync = () => {
    const values = new URL(window.location.href).searchParams.getAll(this.param);
    this.input.checked = values.includes('1') && !values.includes('0');
  };
}

/** Height of the compact header plus the sticky Filter bar (when shown): what covers the top of the list. */
function stickyOffset() {
  const bar = /** @type {HTMLElement | null} */ (document.querySelector('.facets-toggle'));
  const compact = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--cfs-compact-offset')) || 64;
  return compact + (bar?.getClientRects().length ? bar.offsetHeight : 0);
}

/**
 * The product at the top of the screen, and those after it, when the shopper has scrolled into the
 * results; null near the top of the page (nothing to keep).
 */
function captureAnchor() {
  const list = /** @type {HTMLElement | null} */ (document.querySelector('results-list[section-id]'));
  const sectionId = list?.getAttribute('section-id');
  const grid = document.querySelector('.main-collection-grid, .product-grid');
  if (!list || !sectionId || !grid) return null;
  const offset = stickyOffset();
  if (grid.getBoundingClientRect().top >= offset) return null;
  const items = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('.product-grid__item[data-product-id]')]);
  const index = items.findIndex((el) => el.getBoundingClientRect().bottom > offset + 1);
  if (index === -1) return null;
  const first = items[index];
  return {
    list,
    sectionId,
    ids: items.slice(index, index + 48).map((el) => /** @type {string} */ (el.dataset.productId)),
    page: Number(first.dataset.page) || 1,
    top: first.getBoundingClientRect().top,
  };
}

if (!customElements.get('cfs-stock-toggle')) customElements.define('cfs-stock-toggle', CfsStockToggle);

// Back-button position for CFS cards in infinite-scroll grids.
document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target).closest?.('cfs-product-card a[href*="/products/"]');
  if (!link || window.Shopify?.designMode) return;
  const item = /** @type {HTMLElement | null} */ (link.closest('results-list[infinite-scroll="true"] li[id][data-page]'));
  if (!item) return;
  const url = new URL(window.location.href);
  url.hash = item.id;
  url.searchParams.set('page', item.dataset.page ?? '1');
  history.replaceState(history.state, '', url.toString());
});

// Re-render results when Back/Forward changes the query (filters, sort, page), not for hash-only moves.
// The last query is tracked through pushState/replaceState because Horizon updates the URL itself.
let lastQuery = window.location.search;
for (const method of /** @type {const} */ (['pushState', 'replaceState'])) {
  const original = history[method];
  history[method] = function (...args) {
    const result = original.apply(this, /** @type {any} */ (args));
    lastQuery = window.location.search;
    return result;
  };
}
window.addEventListener('popstate', () => {
  if (window.location.search === lastQuery) return;
  lastQuery = window.location.search;
  const list = document.querySelector('results-list[section-id]');
  const sectionId = list?.getAttribute('section-id');
  if (sectionId) sectionRenderer.renderSection(sectionId, { cache: false });
});

// Back to the first results after a filter change (owner, 10 Oct 2026). Near the bottom of a long list,
// a filter that shrinks the results left the shopper looking at the footer. Changes made in the filter
// drawer wait until "See items" closes it. Only scrolls up, and only when the top of the results is out
// of view. Hide out of stock keeps the shopper's place instead (CfsStockToggle), so its updates (made
// near the top of the list) find the results already in view and don't move.
let resultsChangedInDrawer = /** @type {Promise<unknown> | null} */ (null);
const filtersDrawerOpen = () => !!document.querySelector('#filters-drawer dialog[open]');

function scrollToResults() {
  const grid = /** @type {HTMLElement | null} */ (document.querySelector('.main-collection-grid, .product-grid'));
  if (!grid) return;
  const top = grid.getBoundingClientRect().top - stickyOffset();
  if (top >= 0) return;
  const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollBy({ top, behavior: smooth ? 'smooth' : 'instant' });
}

for (const name of [StandardEvents.collectionUpdate, StandardEvents.searchUpdate]) {
  document.addEventListener(name, (event) => {
    const done = /** @type {{ promise?: Promise<unknown> }} */ (/** @type {unknown} */ (event)).promise ?? Promise.resolve();
    if (filtersDrawerOpen()) {
      resultsChangedInDrawer = done;
      return;
    }
    done.then(() => requestAnimationFrame(scrollToResults)).catch(() => {});
  });
}

document.addEventListener(
  'close',
  (event) => {
    const dialog = /** @type {HTMLElement} */ (event.target);
    if (!resultsChangedInDrawer || !dialog.closest?.('#filters-drawer')) return;
    const done = resultsChangedInDrawer;
    resultsChangedInDrawer = null;
    done.then(() => requestAnimationFrame(scrollToResults)).catch(() => {});
  },
  true
);
